import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { DecisionsBadge, isDecisionsModel } from '@/components/DecisionsBadge';
import { Dialog } from '@/components/Dialog';
import { Icon } from '@/components/Icon';
import { AddModelsDialog } from '@/components/AddModelsDialog';
import { AcceptsChips, PriceInOut, apiErrorMessage, discoverSuppliers, needsAttention } from '@/components/modelBits';
import { fmtDate, fmtNumber } from '@/lib/format';
import type { AdminModel, GatewayDefault, ReconcileResult } from '@/types';

type TypeFilter = 'all' | 'llm' | 'decisions';
type StatusFilter = '' | 'enabled' | 'disabled' | 'deactivated' | 'attention';

const statusOptions: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'attention', label: 'Needs attention' }
];

/**
 * The model catalogue: one read-only table with a filter bar. Editing lives on each model's own
 * page; adding lives in the Add models dialog. Filters that the sidebar links to (status, the add
 * dialog) are kept in the URL so those links work.
 */
export function Models() {
  const api = useApi();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const models = useQuery({ queryKey: ['admin-models'], queryFn: api.adminListModels });
  const suppliers = useQuery({ queryKey: ['admin-suppliers'], queryFn: api.adminListSuppliers });
  const gatewayDefault = useQuery({ queryKey: ['gateway-default'], queryFn: api.adminGetGatewayDefault });

  const [search, setSearch] = useState('');
  const [supplier, setSupplier] = useState('');
  const [type, setType] = useState<TypeFilter>('all');
  const status = (params.get('status') ?? '') as StatusFilter;
  const addOpen = params.has('add');
  const [defaultOpen, setDefaultOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key); else next.set(key, value);
    setParams(next, { replace: key === 'status' });
  };

  const all = models.data ?? [];

  const supplierOptions = useMemo(() => {
    const names = new Set<string>();
    for (const s of suppliers.data ?? []) names.add(s.supplier);
    for (const m of all) names.add(m.supplier);
    return [...names].sort();
  }, [suppliers.data, all]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter(m => q === '' || m.displayName.toLowerCase().includes(q) || m.modelId.toLowerCase().includes(q))
      .filter(m => supplier === '' || m.supplier === supplier)
      .filter(m => type === 'all' || (type === 'decisions') === isDecisionsModel(m))
      .filter(m => {
        switch (status) {
          case 'enabled': return m.enabled;
          case 'disabled': return !m.enabled;
          case 'deactivated': return !!m.deactivatedAtUtc;
          case 'attention': return needsAttention(m);
          default: return true;
        }
      })
      .sort((a, b) => a.supplier.localeCompare(b.supplier) || a.displayName.localeCompare(b.displayName));
  }, [all, search, supplier, type, status]);

  const isDefault = (m: AdminModel) =>
    gatewayDefault.data?.supplier === m.supplier && gatewayDefault.data?.modelId === m.modelId;
  const defaultModel = all.find(isDefault);
  const enabledCount = all.filter(m => m.enabled).length;
  const attentionCount = all.filter(needsAttention).length;

  if (models.isLoading) return <div className="placeholder">Loading models...</div>;
  if (models.error) return <div className="error-banner">Could not load models: {(models.error as Error).message}</div>;

  return (
    <>
      <div className="crumbs">Administration / Models</div>
      <div className="page-header">
        <div>
          <h1>Models</h1>
          <div className="sub">The models callers can use, and what they cost per million tokens</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn-secondary" onClick={() => setReconcileOpen(true)}>
            Check for removed models
          </button>
          <button type="button" className="btn-primary" onClick={() => setParam('add', '1')}>
            Add models
          </button>
        </div>
      </div>

      <div className="summary-strip">
        <div className="summary-tile">
          <div style={{ minWidth: 0 }}>
            <div className="summary-label">Gateway default</div>
            <div>
              <span className="summary-value">
                {defaultModel?.displayName
                  ?? (gatewayDefault.data?.modelId ? `${gatewayDefault.data.supplier}/${gatewayDefault.data.modelId}` : 'No default')}
              </span>{' '}
              <span className="summary-note">· used when a request names no model</span>
            </div>
          </div>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setDefaultOpen(true)}>Change</button>
        </div>
        <div className="summary-tile">
          <div>
            <div className="summary-label">Enabled</div>
            <span className="summary-value">{enabledCount}</span> <span className="summary-note">of {all.length}</span>
          </div>
        </div>
        <button
          type="button"
          className={`summary-tile${attentionCount > 0 ? ' attention' : ''}`}
          style={{ textAlign: 'left', cursor: attentionCount > 0 ? 'pointer' : 'default', font: 'inherit', color: 'inherit' }}
          onClick={() => attentionCount > 0 && setParam('status', 'attention')}
          aria-label={`${attentionCount} need attention: deactivated with no fallback${attentionCount > 0 ? '. Show them' : ''}`}>
          <div>
            <div className="summary-label">Needs attention</div>
            <span className="summary-value">{attentionCount}</span>{' '}
            <span className="summary-note">deactivated with no fallback</span>
          </div>
        </button>
      </div>

      <div className="card">
        <div className="filter-bar">
          <input
            type="search"
            className="input"
            placeholder="Search name or model ID"
            aria-label="Search models"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="select" aria-label="Supplier" value={supplier} onChange={e => setSupplier(e.target.value)}>
            <option value="">All suppliers</option>
            {supplierOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="seg-group" role="group" aria-label="Model type">
            {([['all', 'All types'], ['llm', 'LLMs'], ['decisions', 'Decisions']] as const).map(([v, label]) => (
              <button key={v} type="button" className={type === v ? 'seg on' : 'seg'} aria-pressed={type === v}
                onClick={() => setType(v)}>
                {label}
              </button>
            ))}
          </div>
          <select className="select" aria-label="Status" value={status} onChange={e => setParam('status', e.target.value)}>
            {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="filter-count" aria-live="polite">{rows.length} model{rows.length === 1 ? '' : 's'}</span>
        </div>

        <table className="data-table models-table">
          <thead>
            <tr>
              <th>Model</th>
              <th>Supplier</th>
              <th>Type</th>
              <th className="num">In / out per 1M</th>
              <th className="num">Context</th>
              <th>Accepts</th>
              <th>Enabled</th>
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="placeholder">
                {all.length === 0 ? 'No models configured yet. Use Add models to add the first one.' : 'No models match these filters.'}
              </td></tr>
            ) : rows.map(m => (
              <ModelRow key={m.id} model={m} all={all} isDefault={isDefault(m)}
                onOpen={() => navigate(`/admin/models/${m.id}`)} />
            ))}
          </tbody>
        </table>
      </div>

      {defaultOpen && (
        <GatewayDefaultDialog models={all} current={gatewayDefault.data} onClose={() => setDefaultOpen(false)} />
      )}
      {reconcileOpen && <ReconcileDialog onClose={() => setReconcileOpen(false)} />}
      {addOpen && (
        <AddModelsDialog supplierOptions={supplierOptions} onClose={() => setParam('add', null)} />
      )}
    </>
  );
}

function ModelRow({ model, all, isDefault, onOpen }: {
  model: AdminModel;
  all: AdminModel[];
  isDefault: boolean;
  onOpen: () => void;
}) {
  const decisions = isDecisionsModel(model);
  const fallback = model.alternativeModelId != null
    ? all.find(a => String(a.id) === String(model.alternativeModelId))
    : undefined;

  return (
    <tr onClick={onOpen}>
      <td>
        <div className="row-title">
          <Link to={`/admin/models/${model.id}`} onClick={e => e.stopPropagation()}>{model.displayName}</Link>
          {isDefault && <span className="pill pill-success pill-sm" style={{ marginLeft: 6 }}>Default</span>}
          {model.deactivatedAtUtc && (
            needsAttention(model)
              ? <span className="pill pill-attention pill-sm" style={{ marginLeft: 6 }}
                  title={model.deactivationReason ?? 'Deactivated'}>Deactivated · no fallback</span>
              : <span className="pill pill-disabled pill-sm" style={{ marginLeft: 6 }}
                  title={model.deactivationReason ?? 'Deactivated'}>
                  Deactivated · falls back to {fallback?.displayName ?? 'another model'}
                </span>
          )}
        </div>
        <div className="row-sub">{model.modelId}</div>
      </td>
      <td>{model.supplier}</td>
      <td>{decisions ? <DecisionsBadge /> : <span className="pill pill-kind pill-sm">LLM</span>}</td>
      <td className="num" style={{ whiteSpace: 'nowrap' }}>
        <PriceInOut model={model} />
        {model.longContextThresholdTokens != null && (
          <div className="row-sub" style={{ fontFamily: 'inherit' }}>
            higher above {fmtNumber(model.longContextThresholdTokens)}
          </div>
        )}
      </td>
      <td className="num">{model.maxContextTokens == null ? '—' : fmtNumber(model.maxContextTokens)}</td>
      <td>
        {decisions
          ? <AcceptsChips images={model.supportsImageInput} />
          : <AcceptsChips images={model.supportsImageInput} documents={model.supportsDocumentInput}
              fileTasks={model.supportsFileTasks} effort={model.supportsEffort} />}
      </td>
      <td onClick={e => e.stopPropagation()}>
        <EnabledSwitch model={model} />
      </td>
      <td className="row-open" aria-hidden="true"><Icon name="chevron" size={16} /></td>
    </tr>
  );
}

/**
 * Enable/disable from the list. Disabling carries the model's saved fallback, exactly as the old
 * row did with its fallback picker — so callers asking for it are served by that model instead.
 */
function EnabledSwitch({ model }: { model: AdminModel }) {
  const api = useApi();
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.adminSetModelEnabled(model.id, {
        enabled,
        alternativeModelId: enabled ? undefined : model.alternativeModelId ?? undefined
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-models'] })
  });

  return (
    <>
      <label className="switch">
        <input
          type="checkbox"
          checked={model.enabled}
          disabled={toggle.isPending}
          onChange={e => toggle.mutate(e.target.checked)}
          aria-label={`Enable ${model.displayName}`}
        />
        <span className="track"></span>
      </label>
      {toggle.error && (
        <div className="field-error" role="alert" style={{ maxWidth: 160 }}>{apiErrorMessage(toggle.error)}</div>
      )}
    </>
  );
}

/** Gateway default model: applied to completion requests that omit the supplier/model. */
function GatewayDefaultDialog({ models, current, onClose }: {
  models: AdminModel[];
  current: GatewayDefault | undefined;
  onClose: () => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  // Completions refuse Decisions models, so they can't be the completion default.
  const enabled = models.filter(m => m.enabled && !isDecisionsModel(m));
  const currentKey = current?.supplier && current?.modelId ? `${current.supplier}/${current.modelId}` : '';
  const [value, setValue] = useState(currentKey);

  const save = useMutation({
    mutationFn: (key: string) => {
      if (key === '') return api.adminSetGatewayDefault({ supplier: null, modelId: null });
      const slash = key.indexOf('/');
      return api.adminSetGatewayDefault({ supplier: key.slice(0, slash), modelId: key.slice(slash + 1) });
    },
    onSuccess: d => { qc.setQueryData(['gateway-default'], d); onClose(); }
  });

  return (
    <Dialog
      title="Gateway default model"
      onClose={onClose}
      describedBy="gateway-default-desc"
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" onClick={() => save.mutate(value)}
            disabled={save.isPending || value === currentKey}>
            {save.isPending ? 'Saving...' : 'Save'}
          </button>
        </>
      }>
      <p id="gateway-default-desc" className="text-muted" style={{ marginBottom: 14, fontSize: '.9rem' }}>
        Applied to completion requests that omit the supplier/model, so clients can target "the
        gateway's pick" instead of pinning a model. Choose "No default" to require an explicit model on
        every request. Decisions models can't be the default.
      </p>
      <div className="field">
        <label htmlFor="gateway-default-model">Default model</label>
        <select id="gateway-default-model" data-autofocus value={value} onChange={e => setValue(e.target.value)}>
          <option value="">No default</option>
          {enabled.map(m => {
            const key = `${m.supplier}/${m.modelId}`;
            return <option key={key} value={key}>{m.supplier}/{m.displayName}</option>;
          })}
        </select>
      </div>
      {save.error && <div className="error-banner" role="alert">{apiErrorMessage(save.error)}</div>}
      {current?.updatedBy && (
        <div className="text-muted" style={{ fontSize: '.8rem' }}>
          Last set by {current.updatedBy}{current.updatedAtUtc ? ` · ${fmtDate(current.updatedAtUtc)}` : ''}
        </div>
      )}
    </Dialog>
  );
}

/** Deactivate catalogue models a supplier no longer lists. */
function ReconcileDialog({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const [supplier, setSupplier] = useState('');

  const reconcile = useMutation<ReconcileResult, unknown, string>({
    mutationFn: s => api.adminReconcileModels(s),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-models'] })
  });
  const result = reconcile.data;

  return (
    <Dialog
      title="Check for removed models"
      onClose={onClose}
      describedBy="reconcile-desc"
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
          <button type="button" className="btn-primary" onClick={() => reconcile.mutate(supplier)}
            disabled={supplier === '' || reconcile.isPending}>
            {reconcile.isPending ? 'Checking...' : 'Check now'}
          </button>
        </>
      }>
      <p id="reconcile-desc" className="text-muted" style={{ marginBottom: 14, fontSize: '.9rem' }}>
        Compares your enabled catalogue against the supplier's live list and deactivates any it no longer
        offers. A deactivated model keeps working for callers only if it has a fallback (set on the
        model's page, under "If this model is switched off"). OpenRouter is exempt — it isn't bulk-listed.
      </p>
      <div className="field">
        <label htmlFor="reconcile-supplier">Supplier</label>
        <select id="reconcile-supplier" data-autofocus value={supplier} onChange={e => setSupplier(e.target.value)}>
          <option value="">Select a supplier...</option>
          {discoverSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {reconcile.isError && <div className="error-banner" role="alert">{apiErrorMessage(reconcile.error)}</div>}
      {result && !reconcile.isError && (
        result.deactivated.length === 0 ? (
          <div className="info-banner" role="status">Nothing to do — every enabled model is still offered.</div>
        ) : (
          <div role="status">
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Deactivated {result.deactivated.length} model{result.deactivated.length > 1 ? 's' : ''}:
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '.88rem' }}>
              {result.deactivated.map(d => (
                <li key={d.id}>
                  <Link to={`/admin/models/${d.id}`} onClick={onClose}>{d.displayName}</Link>{' '}
                  <span className="text-muted">({d.modelId})</span>
                  {!d.hasAlternative && (
                    <span className="warn-text"> — open it and set a fallback so callers keep working</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )
      )}
    </Dialog>
  );
}
